/**
 * VEXOS Client-Facing Sales Calculator — Pricing Configuration
 *
 * This is the SINGLE source of truth for all rates used by the new
 * client-facing sales calculator. Shared config is now persisted to
 * MongoDB at `/api/vexos-pricing-config` (see helpers at the bottom of
 * this file). localStorage is retained only as a legacy migration cache.
 */
import { apiClient } from "../../App";
import { VR_CONFIGURATOR } from "./vrConfigurator";
import { PRICE_OVERRIDE_LINES } from "./priceOverrideLines";

const TBD = 9999; // Visual sentinel for unwired rates. Replace each occurrence with real value.

// ─── ONE-TIME SETUP FEE (tiered by unit count) ──────────────────────────
// Wired to existing Pricing Admin tiers — these match what Sales actually
// quotes today, so keep them in sync if the main tiers change.
export const SETUP_FEE_TIERS = [
  { minUnits: 0,   maxUnits: 99,   fee: 10000, label: "Under 100 units"  },
  { minUnits: 100, maxUnits: 300,  fee: 15000, label: "101–300 units"    },
  { minUnits: 301, maxUnits: null, fee: 20000, label: "300+ units"       },
];

// ─── MONTHLY SUBSCRIPTION FEE (tiered by unit count) ────────────────────
// Mirrors the existing Monthly Platform Fee tiers (per user spec).
export const MONTHLY_SUBSCRIPTION_TIERS = [
  { minUnits: 0,   maxUnits: 99,   monthlyRate: 2500, label: "Under 100 units" },
  { minUnits: 100, maxUnits: 300,  monthlyRate: 5000, label: "101–300 units"   },
  { minUnits: 301, maxUnits: null, monthlyRate: 7500, label: "300+ units"      },
];

// VEXOS is sold on 12-month terms only. The pricing matrix still carries
// 24/36 columns so quotes saved on those terms keep pricing correctly.
export const CONTRACT_TERM_OPTIONS = [12]; // months

// After the 12-month term the platform stays live on a month-to-month,
// cancel-anytime maintenance fee. The rep can hand-set it per quote (saved as
// calc_snapshot.postTermMonthly); these are the defaults. Mirrors
// POST_TERM_MAINTENANCE_* in backend/routers/vexos_proposal_pdf.py.
export const POST_TERM_MAINTENANCE_MONTHLY = 750;
export const POST_TERM_MAINTENANCE_MONTHLY_SFH = 250;
export const defaultPostTermMonthly = (mode) =>
  mode === "sfh" ? POST_TERM_MAINTENANCE_MONTHLY_SFH : POST_TERM_MAINTENANCE_MONTHLY;

/** The maintenance fee a saved quote states: the rep's figure if one was set,
 *  else the default for its project type. */
export const postTermMonthlyOf = (quote) => {
  const v = Number(quote?.calc_snapshot?.postTermMonthly);
  if (quote?.calc_snapshot?.postTermMonthly != null && Number.isFinite(v) && v >= 0) return Math.round(v);
  return defaultPostTermMonthly(quote?.project_type);
};

// ─── SFH (SINGLE-FAMILY HOME) FIXED PRICING ─────────────────────────────
// SFH engagements are flat-priced — no tiers, no unit/floor logic. The
// rep just toggles SFH and the calculator emits the fixed offer. Both
// values are admin-editable via the SFH section in VexosPricingAdmin.
// SFH default values — kept here for the resolver below. The canonical
// admin-editable copy lives in `ADMIN_DEFAULTS.sfh` further down.
export const SFH_DEFAULTS = {
  setupFee: 10000,           // One-time VEXOS Setup Fee for SFH engagements
  monthlySubscription: 1500, // Monthly VEXOS subscription for SFH engagements
};

// Live mutable SFH config singleton. `applyAdminToConfigs()` overwrites
// these values on every admin save; the calculator reads via `getSFHPricing()`.
// Starts pre-seeded with the canonical defaults so first-load (before any
// admin sync) still produces sensible numbers.
export const SFH_CONFIG = {
  setupFee: SFH_DEFAULTS.setupFee,
  monthlySubscription: SFH_DEFAULTS.monthlySubscription,
};

/**
 * Resolve the current SFH pricing. Reads from the live `SFH_CONFIG`
 * singleton (kept in sync by `applyAdminToConfigs()` on every admin
 * save), falling back to the canonical defaults if a value was wiped
 * or cleared to 0.
 *
 * @returns {{ setupFee: number, monthlySubscription: number }}
 */
export function getSFHPricing() {
  const setupFee = Number(SFH_CONFIG.setupFee);
  const monthlySubscription = Number(SFH_CONFIG.monthlySubscription);
  return {
    setupFee: Number.isFinite(setupFee) && setupFee > 0 ? setupFee : SFH_DEFAULTS.setupFee,
    monthlySubscription:
      Number.isFinite(monthlySubscription) && monthlySubscription > 0
        ? monthlySubscription
        : SFH_DEFAULTS.monthlySubscription,
  };
}

// ─── ORBIT STRUCTURE ───────────────────────────────────────────────────
// Two kinds of orbit exist in a quote:
//   • the BASE orbit (building / home / community) — always included with
//     BASE_ORBIT_KEYFRAMES keyframes, extras billed per keyframe;
//   • SUBORBITS (formerly "Master Orbit") — sold via the Additional Orbit
//     line. A suborbit is only buildable with at least two keyframes, so
//     SUBORBIT_INCLUDED_KEYFRAMES comes bundled in the per-orbit price and
//     the rep may add up to SUBORBIT_MAX_EXTRA_KEYFRAMES more to it.
export const BASE_ORBIT_KEYFRAMES = 4;
export const SUBORBIT_INCLUDED_KEYFRAMES = 2;
export const SUBORBIT_MAX_EXTRA_KEYFRAMES = 2;

// ─── MASTER PLAN (a Community variant) ─────────────────────────────────
// A master plan is a community sold as an expanded package: the community
// orbit is its MASTER orbit, and it comes with MASTER_PLAN_INCLUDED_SUBORBITS
// suborbits built in, each at MASTER_PLAN_SUBORBIT_KEYFRAMES keyframes (the
// suborbit's bundled two plus two extra). The rep can add Additional Orbits
// beyond those as usual. Only the setup fee is its own (flat, admin-editable);
// the monthly follows the community unit tiers like any other community.
//
// A master plan saves as project_type "community" with `master_plan: true`, so
// every reader that keys off the c_-prefixed community fields keeps working.
export const MASTER_PLAN_INCLUDED_SUBORBITS = 2;
export const MASTER_PLAN_SUBORBIT_KEYFRAMES = 4;
export const MASTER_PLAN_SUBORBIT_EXTRA_KF = MASTER_PLAN_SUBORBIT_KEYFRAMES - SUBORBIT_INCLUDED_KEYFRAMES;
export const MASTER_PLAN_DEFAULTS = {
  setupFee: 30000,
};
// Live mutable singleton, overwritten by `applyAdminToConfigs()`.
export const MASTER_PLAN_CONFIG = {
  setupFee: MASTER_PLAN_DEFAULTS.setupFee,
};
export function getMasterPlanSetupFee() {
  const fee = Number(MASTER_PLAN_CONFIG.setupFee);
  return Number.isFinite(fee) && fee > 0 ? fee : MASTER_PLAN_DEFAULTS.setupFee;
}

/** The suborbit list a master plan starts with: its included suborbits at full keyframes. */
export const masterPlanDefaultSubOrbits = () =>
  Array.from({ length: MASTER_PLAN_INCLUDED_SUBORBITS }, () => MASTER_PLAN_SUBORBIT_EXTRA_KF);

/**
 * The part of a master plan's suborbit list the client pays for. The first
 * MASTER_PLAN_INCLUDED_SUBORBITS entries are in the package, and so are their
 * first MASTER_PLAN_SUBORBIT_EXTRA_KF extra keyframes; anything past that bills
 * like any Additional Orbit. Returns { orbits, extraKf } — billable orbit count
 * and billable extra keyframes.
 */
export function masterPlanBillableSubOrbits(subOrbits) {
  const list = Array.isArray(subOrbits) ? subOrbits : [];
  let extraKf = 0;
  list.forEach((n, i) => {
    const kf = Math.max(0, Number(n) || 0);
    extraKf += i < MASTER_PLAN_INCLUDED_SUBORBITS ? Math.max(0, kf - MASTER_PLAN_SUBORBIT_EXTRA_KF) : kf;
  });
  return { orbits: Math.max(0, list.length - MASTER_PLAN_INCLUDED_SUBORBITS), extraKf };
}

// "two" / "three" / "four" — used for the per-suborbit subtext.
const KF_WORDS = ["zero", "one", "two", "three", "four", "five", "six"];
export const keyframeWord = (n) => KF_WORDS[n] ?? String(n);

/** Client-facing subtext under a suborbit: "Includes two keyframes". */
export function suborbitKeyframeSubtext(extraKeyframes = 0) {
  const total = SUBORBIT_INCLUDED_KEYFRAMES + (Number(extraKeyframes) || 0);
  return `Includes ${keyframeWord(total)} keyframe${total === 1 ? "" : "s"}`;
}

// ─── EXTERIOR ADD-ONS ──────────────────────────────────────────────────
export const EXTERIOR_ADDONS = {
  dayToNight: {
    label: "Day-to-Night Upgrade",
    // Built into every orbit in the quote — the base orbit AND any
    // suborbits — so the price scales with the exterior assets sold rather
    // than sitting at a flat per-project rate.
    description: "Every orbit transitions between day and night lighting. Each orbit's included keyframes are priced 1:1; keyframes added to an orbit are half rate.",
    pricePerKeyframe: TBD, // TBD — per-keyframe day-to-night rate
    priceMode: "per_keyframe",
  },
  animatedKeyframes: {
    label: "Animated Keyframes",
    description: "Animate selected keyframes into seamless looping shots for added depth and life.",
    pricePerKeyframe: TBD, // TBD — per-keyframe animation rate
    priceMode: "per_keyframe",
  },
  additionalKeyframes: {
    label: "Additional Keyframes",
    description: "Add keyframes beyond the standard 4 included in the base orbit.",
    pricePerKeyframe: TBD, // TBD — per-additional-keyframe rate
    priceMode: "per_keyframe",
  },
  masterOrbit: {
    // NOTE: internal key stays `masterOrbit` for back-compat with saved
    // quotes and the pricing-admin override (exterior.masterOrbitPerOrbit).
    // The product it sells is a SUBORBIT — "Master Orbit" is retired wording.
    label: "Additional Orbit",
    description: "Add a suborbit beyond the base package — a second flight showing site context, an amenity deck, or another elevation.",
    pricePerOrbit: TBD, // TBD — per-additional-orbit rate
    priceMode: "per_orbit",
    maxQuantity: 5,
  },
  vexosCoverLandingPage: {
    label: "VEXOS Cover Landing Page",
    description: "A branded, project-specific landing page hosted within VEXOS — designed to capture buyer interest and drive direct inquiries.",
    price: 2500, // flat one-time
    priceMode: "flat",
  },
};

// ─── CONCEPTUAL RENDERING — HYBRID WORKFLOW (VEXOS + À LA CARTE) ───────
// The unit is still fully modeled, with AI assisting the design, materials
// and finishes — faster and cheaper than the traditional pipeline, at the
// cost of less room for extensive revisions. Its own section on the VEXOS
// and À La Carte tabs, sold per image at a flat rate — no volume tiers, and
// deliberately NOT pooled with the photoreal render tiers (or the VEXOS 50%
// rendering rate), because the work is a different pipeline.
//
// Replaced "Conceptual Design Renderings" ($749) on 2026-11-03. Internal
// field names stay `conceptual*` (this line began as "Conceptual Exterior
// Modeling"), so quotes saved before the rename re-open unchanged.
export const CONCEPTUAL_MODELING = {
  label: "Conceptual Rendering Hybrid Workflow",
  // Two subtexts, in order: what it is, then the trade-off.
  description: "Our hybrid approach still involves fully modeling the unit, but incorporates AI to assist with the design, materials, and finishes. This allows us to significantly reduce production time and cost while still delivering high-quality visuals.",
  workflowNote: "The main trade-off is slightly less flexibility when it comes to extensive revisions and design adjustments.",
  pricePerRender: 800,
  priceMode: "per_render",
};

// ─── INTERIOR — RENDERINGS ─────────────────────────────────────────────
export const INTERIOR_RENDERING = {
  pricePerRender: TBD, // TBD — per-render rate
  recommendationBands: [
    { maxAvgSqFt: 800,   recommendedRenders: 3 },
    { maxAvgSqFt: 1200,  recommendedRenders: 4 },
    { maxAvgSqFt: 1800,  recommendedRenders: 5 },
    { maxAvgSqFt: null,  recommendedRenders: 6 }, // > 1,800
  ],
};

// ─── INTERIOR — VR TOURS ───────────────────────────────────────────────
// Per-floor-plan point selector: 0 / 2 / 4 / 6 / 8 / 10 points.
// Cost = sum of selected points × pricePerPoint.
export const INTERIOR_VR = {
  pricePerPoint: TBD, // TBD — per-VR-tour-point rate
  pointOptions: [0, 2, 4, 6, 8, 10],
  recommendationBands: [
    { maxAvgSqFt: 800,   recommendedPoints: 4 },
    { maxAvgSqFt: 1400,  recommendedPoints: 6 },
    { maxAvgSqFt: null,  recommendedPoints: 8 }, // > 1,400
  ],
};

// ─── EXTERIOR — VR TOUR ────────────────────────────────────────────────
// A single point-based tour AROUND the building/home/community exterior
// (street-view style). One tour per project — not per floor plan. Priced
// per point off the SAME volume-tier mechanics as Interior VR, but with its
// own admin-editable rate table. Shared across Condo / SFH / Community.
export const EXTERIOR_VR = {
  pricePerPoint: TBD, // TBD — per-exterior-VR-point rate (admin-editable)
  pointOptions: [0, 2, 4, 6, 8, 10],
  recommendedPoints: 4, // flat hint — exterior tours have no per-plan sq ft
};

// ─── AMENITY — RENDERINGS ──────────────────────────────────────────────
export const AMENITY_RENDERING = {
  pricePerRender: TBD, // TBD — per-amenity-render rate
  // Recommendation based on AVERAGE amenity space size = (total sq ft) / (# spaces)
  recommendationBands: [
    { maxAvgSqFt: 500,   recommendedRenders: 1 },
    { maxAvgSqFt: 1000,  recommendedRenders: 2 },
    { maxAvgSqFt: null,  recommendedRenders: 3 }, // > 1,000
  ],
};

// ─── DRONE FEES ────────────────────────────────────────────────────────
// Three flat per-project fees:
//   - captureFee: auto-applied when ANY VR tour (Interior or Amenity) has
//     a selection. Single $5,000 line; does not stack across phases.
//   - interiorDroneShotsFee / amenityDroneShotsFee: optional creative
//     add-on toggles (one per VR section). $5,000 each.
// All three values are admin-editable via PricingAdmin Section 4C.
export const DRONE = {
  captureFee: 5000,
  interiorDroneShotsFee: 5000,
  amenityDroneShotsFee: 5000,
};

// ─── AMENITY — VR TOUR (per amenity space, flat package prices) ────────
// 2 / 4 / 6 / 8 / 10 point packages — flat per-row pricing (no volume tier).
export const AMENITY_VR = {
  packages: { 2: 4500, 4: 9000, 6: 13000, 8: 17000, 10: 21000 },
  pointOptions: [0, 2, 4, 6, 8, 10],
  // Recommendation thresholds use TOTAL amenity sq ft (sum across all spaces),
  // not average. Field name `maxTotalSqFt` reflects this.
  recommendationBands: [
    { maxTotalSqFt: 999,  recommendedPoints: 2 },
    { maxTotalSqFt: 2000, recommendedPoints: 4 },
    { maxTotalSqFt: 4000, recommendedPoints: 6 },
    { maxTotalSqFt: 7000, recommendedPoints: 8 },
    { maxTotalSqFt: null, recommendedPoints: 10 },
  ],
};

// ─── VIDEO ANIMATION (Phase 05) ─────────────────────────────────────────
// Two independent products (AI Motion, Cinematic). Each has 3 duration tiers
// (30/60/90 sec). Mutually exclusive within a product, independent across.
// Motion Package: automatic bonus discount when BOTH products are selected.
export const VIDEO_ANIMATION = {
  aiMotion: {
    label: "AI Motion",
    description: "AI-generated motion video — fast turnaround, high visual impact.",
    durations: { 30: 4500, 60: 8000, 90: 8000 },
  },
  cinematic: {
    label: "Cinematic Animation",
    description: "Full cinematic production — custom camera path, lighting, and scene composition.",
    // ADD-ON prices — used ONLY when the video rides alongside a Vexos
    // package (modeling is already covered by the package). Standalone
    // sales use STANDALONE_VIDEO below instead.
    durations: { 30: 8000, 60: 10000, 90: 12000 },
  },
  motionPackage: {
    enabled: true,
    pct: 10,
  },
};

// ─── STANDALONE VIDEO ANIMATION PRICING (size-first) ────────────────────
// When a video is sold on its OWN (À La Carte, no Vexos package and no other
// products/services in the quote), pricing is SIZE-FIRST: the project size
// dictates which video formats are offered. There is no free-form length
// selection. This is entirely separate from the add-on prices in
// `VIDEO_ANIMATION.cinematic.durations` above, which apply only inside a
// Vexos bundle and are NOT changed by this model.
//
//   • Small  — single home, boutique building, spec house
//              → 30-second hero only
//   • Medium — townhome/SFH community, mid-rise, small condo
//              → 60-second hero only (no add-ons)
//   • Large  — master plan, condo tower, resort, mixed-use
//              → 90-second hero OR 2-minute master cinematic
//              → unlocks the Large add-on menu
//
// Line-item TYPES are tracked separately (spec guardrail #3):
//   • "animation" — new animated seconds (hero, master, unit, penthouse)
//   • "editing"   — edited seconds only (social cut-downs) — never priced
//                   as animation.
//
// Metadata (labels / seconds / which heroes per size / lineType) is static.
// PRICES + the included-cut-down count are admin-editable and live on the
// mutable fields below (overwritten by applyAdminToConfigs).
export const STANDALONE_VIDEO = {
  tiers: {
    S: {
      label: "Small",
      blurb: "Single home, boutique building, spec house",
      heroes: [{ key: "hero30", label: "30-second hero", seconds: 30 }],
      addOnsUnlocked: false,
    },
    M: {
      label: "Medium",
      blurb: "Townhome / SFH community, mid-rise, small condo",
      heroes: [{ key: "hero60", label: "60-second hero", seconds: 60 }],
      addOnsUnlocked: false,
    },
    L: {
      label: "Large",
      blurb: "Master plan, condo tower, resort, mixed-use",
      heroes: [
        { key: "hero90", label: "90-second hero", seconds: 90 },
        { key: "master120", label: "2-minute master cinematic", seconds: 120 },
      ],
      addOnsUnlocked: true,
    },
  },
  // Large-tier add-on metadata. `lineType` distinguishes new animated seconds
  // from editing-only line items.
  addOnMeta: {
    unitInterior:  { label: "20-second unit interior video", seconds: 20, lineType: "animation", qtyHint: "one per typical unit layout" },
    penthouse:     { label: "30-second penthouse / signature video", seconds: 30, lineType: "animation" },
    socialCutdown: { label: "Social cut-down (15–30s vertical/square)", lineType: "editing" },
  },
  // ── Admin-editable prices (mutated by applyAdminToConfigs) ──
  heroPrices: { hero30: 12500, hero60: 18500, hero90: 22500, master120: 28500 },
  addOnPrices: { unitInterior: 3500, penthouse: 5000, socialCutdownPerClip: 500 },
  // How many social cut-downs are bundled free with a master before the
  // per-clip editing rate applies. Admin-editable (spec: "2–3 included").
  socialCutdownsIncluded: 0,
};

// UI ordering for the size cards.
export const STANDALONE_VIDEO_SIZES = ["S", "M", "L"];

export function standaloneTier(size) {
  return STANDALONE_VIDEO.tiers[size] || null;
}
export function standaloneHeroesForSize(size) {
  return standaloneTier(size)?.heroes || [];
}
export function standaloneSizeUnlocksAddOns(size) {
  return !!standaloneTier(size)?.addOnsUnlocked;
}
export function standaloneHeroPrice(heroKey) {
  return Number(STANDALONE_VIDEO.heroPrices[heroKey]) || 0;
}
export function standaloneHeroMeta(size, heroKey) {
  return standaloneHeroesForSize(size).find((h) => h.key === heroKey) || null;
}

/**
 * Compute a standalone video quote from a selection. Returns typed line
 * items (animation vs editing), sub-totals, and the grand total. Social
 * cut-downs list the requested qty, the free-included count, and the
 * billable remainder so the proposal can show them explicitly.
 *
 * sel = { size, heroKey, unitInteriorQty, penthouseQty, socialCutdownQty }
 */
export function computeStandaloneVideo(sel = {}) {
  const empty = { lines: [], animationTotal: 0, editingTotal: 0, total: 0, heroKey: null, heroLabel: null, heroSeconds: 0, sizeLabel: null };
  const tier = standaloneTier(sel.size);
  if (!tier) return empty;
  const hero = standaloneHeroMeta(sel.size, sel.heroKey);
  if (!hero) return { ...empty, sizeLabel: tier.label };

  const lines = [];
  const heroPrice = standaloneHeroPrice(hero.key);
  lines.push({
    type: "animation", key: hero.key, label: hero.label,
    seconds: hero.seconds, qty: 1, unit: heroPrice, amount: heroPrice, isHero: true,
  });

  if (standaloneSizeUnlocksAddOns(sel.size)) {
    const uq = Math.max(0, Math.round(Number(sel.unitInteriorQty) || 0));
    if (uq > 0) {
      const unit = Number(STANDALONE_VIDEO.addOnPrices.unitInterior) || 0;
      lines.push({ type: "animation", key: "unitInterior", label: STANDALONE_VIDEO.addOnMeta.unitInterior.label, seconds: 20, qty: uq, unit, amount: unit * uq });
    }
    const pq = Math.max(0, Math.round(Number(sel.penthouseQty) || 0));
    if (pq > 0) {
      const unit = Number(STANDALONE_VIDEO.addOnPrices.penthouse) || 0;
      lines.push({ type: "animation", key: "penthouse", label: STANDALONE_VIDEO.addOnMeta.penthouse.label, seconds: 30, qty: pq, unit, amount: unit * pq });
    }
    const req = Math.max(0, Math.round(Number(sel.socialCutdownQty) || 0));
    if (req > 0) {
      const included = Math.max(0, Math.round(Number(STANDALONE_VIDEO.socialCutdownsIncluded) || 0));
      const billable = Math.max(0, req - included);
      const unit = Number(STANDALONE_VIDEO.addOnPrices.socialCutdownPerClip) || 0;
      lines.push({
        type: "editing", key: "socialCutdown", label: STANDALONE_VIDEO.addOnMeta.socialCutdown.label,
        qty: req, includedFree: Math.min(included, req), billable, unit, amount: unit * billable,
      });
    }
  }

  const animationTotal = lines.filter((l) => l.type === "animation").reduce((s, l) => s + l.amount, 0);
  const editingTotal = lines.filter((l) => l.type === "editing").reduce((s, l) => s + l.amount, 0);
  return {
    lines, animationTotal, editingTotal, total: animationTotal + editingTotal,
    heroKey: hero.key, heroLabel: hero.label, heroSeconds: hero.seconds, sizeLabel: tier.label,
  };
}

// ─── FLOOR PLANS (Phase 06) ─────────────────────────────────────────────
// Flat rate per plan PLUS independent volume discount tiers for each of
// 2D and 3D (they do not pool for tier qualification).
// Tier 1 default: 6+ plans → 5% off · Tier 2 default: 11+ plans → 10% off.
// 3D is sold under two workflows: "hybrid" (AI Hybrid — the base price3D
// rate, 1 revision round) and "traditional" (full no-AI 3D pipeline,
// 2 revision rounds, exact to spec) which adds traditional3DUpcharge per plan.
export const FLOOR_PLANS = {
  price2D: 500,
  price3D: 750,
  traditional3DUpcharge: 500,
  volumeTiers: {
    tier1MinQty: 6,
    tier1Pct: 5,
    tier2MinQty: 11,
    tier2Pct: 10,
  },
};

// Returns { pct, savings, gross, net } for a given quantity of one floor-plan
// type. Discount applies to the entire qty at the qualifying rate.
export function floorPlanVolumeDiscount(qty, perPlanRate) {
  const n = Math.max(0, Number(qty) || 0);
  const rate = Number(perPlanRate) || 0;
  const gross = n * rate;
  const t = FLOOR_PLANS.volumeTiers;
  let pct = 0;
  if (n >= (Number(t.tier2MinQty) || Infinity)) pct = Number(t.tier2Pct) || 0;
  else if (n >= (Number(t.tier1MinQty) || Infinity)) pct = Number(t.tier1Pct) || 0;
  const savings = Math.round(gross * (pct / 100));
  const net = gross - savings;
  return { pct, savings, gross, net };
}

// Effective per-plan 3D rate for the chosen workflow ("hybrid" | "traditional").
export function floorPlan3DRate(workflow) {
  const base = Number(FLOOR_PLANS.price3D) || 0;
  return workflow === "traditional"
    ? base + (Number(FLOOR_PLANS.traditional3DUpcharge) || 0)
    : base;
}

// ─── LOOKUP HELPERS ────────────────────────────────────────────────────
export function getSetupFee(unitCount) {
  const n = Math.max(0, Number(unitCount) || 0);
  return (SETUP_FEE_TIERS.find(t => n >= t.minUnits && (t.maxUnits === null || n <= t.maxUnits))?.fee) || 0;
}

export function getMonthlyRate(unitCount, term) {
  const n = Math.max(0, Number(unitCount) || 0);
  const t = Number(term) || 12;
  const tier = MONTHLY_SUBSCRIPTION_TIERS.find(x => n >= x.minUnits && (x.maxUnits === null || n <= x.maxUnits));
  if (!tier) return 0;
  return Number((tier.ratesByTerm || {})[t]) || 0;
}

export function recommendInteriorRenders(avgSqFt) {
  const n = Number(avgSqFt) || 0;
  return INTERIOR_RENDERING.recommendationBands.find(b => b.maxAvgSqFt === null || n <= b.maxAvgSqFt)?.recommendedRenders || 0;
}

export function recommendInteriorVR(avgSqFt) {
  const n = Number(avgSqFt) || 0;
  return INTERIOR_VR.recommendationBands.find(b => b.maxAvgSqFt === null || n <= b.maxAvgSqFt)?.recommendedPoints || 0;
}

// Exterior VR is a single per-project tour with no per-plan sq ft to key
// off, so the recommendation is a flat hint rather than a sq-ft band.
export function recommendExteriorVR() {
  return Number(EXTERIOR_VR.recommendedPoints) || 0;
}

export function recommendAmenityRenders(totalAmenitySqFt, amenitySpaceCount) {
  const count = Math.max(1, Number(amenitySpaceCount) || 1);
  const avg = (Number(totalAmenitySqFt) || 0) / count;
  return AMENITY_RENDERING.recommendationBands.find(b => b.maxAvgSqFt === null || avg <= b.maxAvgSqFt)?.recommendedRenders || 0;
}

export function recommendAmenityVRPoints(totalAmenitySqFt, amenitySpaceCount) {
  // Per spec, recommendation is keyed off TOTAL amenity area, not average
  // per space. amenitySpaceCount kept in signature for backwards compat.
  void amenitySpaceCount;
  const total = Math.max(0, Number(totalAmenitySqFt) || 0);
  return AMENITY_VR.recommendationBands.find(b => b.maxTotalSqFt === null || total <= b.maxTotalSqFt)?.recommendedPoints || 0;
}

export function isTBD(value) {
  return value === TBD;
}

/* ════════════════════════════════════════════════════════════════════════
 *  COMMUNITY VARIANT (option C — reuse Condo tier values for now;
 *  swap real Community-specific rates here as they are finalized.)
 *
 *  Naming: Lots (vs Units), Home Models (vs Floor Plans),
 *          Community Orbit (vs Building Orbit).
 *  All quantity defaults remain 0 in the UI; recommendations are hints.
 * ════════════════════════════════════════════════════════════════════════ */

// ─── COMMUNITY · ONE-TIME SETUP FEE (tiered by lot count) ──────────────
export const COMMUNITY_SETUP_FEE_TIERS = [
  { minLots: 0,   maxLots: 99,   fee: 10000, label: "Under 100 lots" },
  { minLots: 100, maxLots: 300,  fee: 15000, label: "101–300 lots"   },
  { minLots: 301, maxLots: null, fee: 20000, label: "300+ lots"      },
];

// ─── COMMUNITY · MONTHLY SUBSCRIPTION FEE (tiered by lot count) ────────
export const COMMUNITY_MONTHLY_SUBSCRIPTION_TIERS = [
  { minLots: 0,   maxLots: 99,   monthlyRate: 2500, label: "Under 100 lots" },
  { minLots: 100, maxLots: 300,  monthlyRate: 5000, label: "101–300 lots"   },
  { minLots: 301, maxLots: null, monthlyRate: 7500, label: "300+ lots"      },
];

// ─── COMMUNITY · EXTERIOR ADD-ONS ──────────────────────────────────────
// Same shape & rates as Condo today — Sam can split these out if Community
// pricing diverges from Condo. Replace TBD sentinels in this file.
export const COMMUNITY_EXTERIOR_ADDONS = {
  dayToNight: {
    label: "Day-to-Night Upgrade",
    description: "Every orbit transitions between day and night lighting. Each orbit's included keyframes are priced 1:1; keyframes added to an orbit are half rate.",
    pricePerKeyframe: TBD,
    priceMode: "per_keyframe",
  },
  animatedKeyframes: {
    label: "Animated Keyframes",
    description: "Animate selected keyframes into seamless looping shots for added depth and life.",
    pricePerKeyframe: TBD,
    priceMode: "per_keyframe",
  },
  additionalKeyframes: {
    label: "Additional Keyframes",
    description: "Add keyframes beyond the standard 4 included in the base Community Orbit.",
    pricePerKeyframe: TBD,
    priceMode: "per_keyframe",
  },
  masterOrbit: {
    // Internal key stays `masterOrbit` — see note on EXTERIOR_ADDONS.masterOrbit.
    label: "Additional Orbit",
    description: "Add a suborbit beyond the base Community Orbit — a second flight showing community context, an amenity area, or another neighbourhood.",
    pricePerOrbit: TBD,
    priceMode: "per_orbit",
    maxQuantity: 5,
  },
  vexosCoverLandingPage: {
    label: "VEXOS Cover Landing Page",
    description: "A branded, project-specific landing page hosted within VEXOS — designed to capture buyer interest and drive direct inquiries.",
    price: 2500,
    priceMode: "flat",
  },
};

// ─── COMMUNITY · HOME MODEL RENDERINGS ─────────────────────────────────
// Recommendation bands mirror Condo interior bands; tune as needed.
export const HOME_MODEL_RENDERING = {
  pricePerRender: TBD,
  recommendationBands: [
    { maxAvgSqFt: 1500, recommendedRenders: 4 },
    { maxAvgSqFt: 2500, recommendedRenders: 5 },
    { maxAvgSqFt: 3500, recommendedRenders: 6 },
    { maxAvgSqFt: null, recommendedRenders: 8 }, // > 3,500
  ],
};

// ─── COMMUNITY · HOME MODEL VR TOURS ───────────────────────────────────
// Per-home-model point selector: 0 / 2 / 4 / 6 / 8 / 10 points.
// Cost = sum of selected points × pricePerPoint.
export const HOME_MODEL_VR = {
  pricePerPoint: TBD,
  pointOptions: [0, 2, 4, 6, 8, 10],
  recommendationBands: [
    { maxAvgSqFt: 1500, recommendedPoints: 6 },
    { maxAvgSqFt: 2500, recommendedPoints: 8 },
    { maxAvgSqFt: null, recommendedPoints: 10 }, // > 2,500
  ],
};

// ─── COMMUNITY · AMENITY BUILDING/SPACE RENDERINGS ─────────────────────
// Recommendation is on AVERAGE amenity-space size (total sq ft / # spaces).
export const COMMUNITY_AMENITY_RENDERING = {
  pricePerRender: TBD,
  recommendationBands: [
    { maxAvgSqFt: 800,   recommendedRenders: 2 },
    { maxAvgSqFt: 1500,  recommendedRenders: 3 },
    { maxAvgSqFt: null,  recommendedRenders: 4 }, // > 1,500
  ],
};

// ─── COMMUNITY · AMENITY VR TOUR (per amenity building/space, packages) ─
export const COMMUNITY_AMENITY_VR = {
  packages: { 2: 4500, 4: 9000, 6: 13000, 8: 17000, 10: 21000 },
  pointOptions: [0, 2, 4, 6, 8, 10],
  recommendationBands: [
    { maxTotalSqFt: 999,  recommendedPoints: 2 },
    { maxTotalSqFt: 2000, recommendedPoints: 4 },
    { maxTotalSqFt: 4000, recommendedPoints: 6 },
    { maxTotalSqFt: 7000, recommendedPoints: 8 },
    { maxTotalSqFt: null, recommendedPoints: 10 },
  ],
};

// ─── COMMUNITY · LOOKUP HELPERS ────────────────────────────────────────
export function getCommunitySetupFee(lotCount) {
  const n = Math.max(0, Number(lotCount) || 0);
  return (COMMUNITY_SETUP_FEE_TIERS.find(t => n >= t.minLots && (t.maxLots === null || n <= t.maxLots))?.fee) || 0;
}

export function getCommunityMonthlyRate(lotCount, term) {
  const n = Math.max(0, Number(lotCount) || 0);
  const t = Number(term) || 12;
  const tier = COMMUNITY_MONTHLY_SUBSCRIPTION_TIERS.find(x => n >= x.minLots && (x.maxLots === null || n <= x.maxLots));
  if (!tier) return 0;
  return Number((tier.ratesByTerm || {})[t]) || 0;
}

export function recommendHomeModelRenders(avgSqFt) {
  const n = Number(avgSqFt) || 0;
  return HOME_MODEL_RENDERING.recommendationBands.find(b => b.maxAvgSqFt === null || n <= b.maxAvgSqFt)?.recommendedRenders || 0;
}

export function recommendHomeModelVR(avgSqFt) {
  const n = Number(avgSqFt) || 0;
  return HOME_MODEL_VR.recommendationBands.find(b => b.maxAvgSqFt === null || n <= b.maxAvgSqFt)?.recommendedPoints || 0;
}

export function recommendCommunityAmenityRenders(totalAmenitySqFt, amenitySpaceCount) {
  const count = Math.max(1, Number(amenitySpaceCount) || 1);
  const avg = (Number(totalAmenitySqFt) || 0) / count;
  return COMMUNITY_AMENITY_RENDERING.recommendationBands.find(b => b.maxAvgSqFt === null || avg <= b.maxAvgSqFt)?.recommendedRenders || 0;
}

export function recommendCommunityAmenityVRPoints(totalAmenitySqFt, amenitySpaceCount) {
  void amenitySpaceCount;
  const total = Math.max(0, Number(totalAmenitySqFt) || 0);
  return COMMUNITY_AMENITY_VR.recommendationBands.find(b => b.maxTotalSqFt === null || total <= b.maxTotalSqFt)?.recommendedPoints || 0;
}


/* ════════════════════════════════════════════════════════════════════════
 *  PRICING ADMIN — Volume Tiers + localStorage Overrides
 *
 *  The admin manages one global pricing model that flows to BOTH Condo and
 *  Community configs (per spec: "applies to both calculators"). Volume tiers
 *  determine the per-unit rate based on TOTAL quantity in the order — the
 *  qualifying rate is then applied to the entire quantity (not blended).
 * ═══════════════════════════════════════════════════════════════════════ */

const ADMIN_STORAGE_KEY = "vexos-pricing-admin-v1";
const ADMIN_PIN_KEY = "vexos-pricing-admin-pin-v1";
const DEFAULT_PIN = "0000";

// Placeholder guardrail bands, per line, until Bernard sets real ones.
export const GUARDRAIL_DEFAULT_FIRST_STEP_PCT = 10;
export const GUARDRAIL_DEFAULT_FLOOR_PCT = 20;

// Initial admin state — used when localStorage is empty or after a reset.
export const ADMIN_DEFAULTS = {
  setup: {
    tier1MaxUnits: 99,
    tier1Fee: 10000,
    tier2MaxUnits: 299,
    tier2Fee: 15000,
    tier3Fee: 25000,
  },
  // Monthly subscription is now a 3×3 matrix (scale tier × term length).
  // tier1/tier2/tier3 maxUnits are reused from setup tier thresholds.
  monthly: {
    tier1MaxUnits: 99,
    tier2MaxUnits: 299,
    rates: {
      tier1: { 12: 2800, 24: 2500, 36: 2200 },
      tier2: { 12: 5500, 24: 4800, 36: 4200 },
      tier3: { 12: 8500, 24: 7500, 36: 6500 },
    },
  },
  exterior: {
    // Day-to-night, per Bernard 2026-08-28: the keyframes an orbit INCLUDES
    // price 1:1 so the figure reads off the orbit ("base orbit is 4 keyframes,
    // so day-to-night is $4,000"; a 2-keyframe suborbit is $2,000). Keyframes
    // ADDED on top are half rate.
    //
    // `dayToNightPerKf` keeps its name rather than becoming
    // `dayToNightIncludedPerKf` because saved admin configs and quotes already
    // carry that key — renaming it would silently reset every override to the
    // default. `dayToNight` is the long-retired flat rate, still read only to
    // migrate configs saved before any of this.
    dayToNightPerKf: 1000,      // included keyframes, 1:1
    dayToNightExtraPerKf: 500,  // keyframes added to an orbit
    dayToNight: 4000,
    additionalKeyframePerKf: 1000,
    animatedKeyframePerKf: 500,
    masterOrbitPerOrbit: 2500,
    vexosCoverLandingPage: 2500,
  },
  // Conceptual Rendering Hybrid Workflow — flat per-image rate (VEXOS + À La
  // Carte). A new key, not `conceptual`: configs saved in prod carry the old
  // line's $749 under `conceptual`, and that would beat this default.
  conceptualHybrid: { pricePerRender: 800 },
  interiorRendering: { tier1MaxQty: 10, tier1Rate: 1800, tier2MaxQty: 25, tier2Rate: 1500, tier3Rate: 1200 },
  interiorVR:        { tier1MaxQty: 10, tier1Rate: 2000, tier2MaxQty: 25, tier2Rate: 1700, tier3Rate: 1500 },
  exteriorVR:        { tier1MaxQty: 10, tier1Rate: 2000, tier2MaxQty: 25, tier2Rate: 1700, tier3Rate: 1500 },
  amenityRendering:  { tier1MaxQty: 5,  tier1Rate: 2000, tier2MaxQty: 10, tier2Rate: 1700, tier3Rate: 1400 },
  // ─── VEXOS-INCLUDED RENDERING RATES ─────────────────────────────────
  // Renderings that ride inside a VEXOS engagement bill off their own rate
  // card — half the standalone visualization rates above. The two tables
  // above stay the standalone (À La Carte) rate card and are NOT touched by
  // anything here. Tier thresholds mirror their standalone counterparts so
  // the same volume breaks apply in both contexts.
  // Since 2026-11-02 a flat VEXOS_INCLUDED_RENDER_RATE ($1,200) on every
  // tier — VEXOS unlocks one discounted rendering price, not half of a
  // volume ladder (Cole / Bernard). Still fully editable in Section 3B.
  vexosInteriorRendering: { tier1MaxQty: 10, tier1Rate: 1200, tier2MaxQty: 25, tier2Rate: 1200, tier3Rate: 1200 },
  vexosAmenityRendering:  { tier1MaxQty: 5,  tier1Rate: 1200, tier2MaxQty: 10, tier2Rate: 1200, tier3Rate: 1200 },
  // Amenity VR — flat package prices per amenity space selection.
  amenityVRPackages: { 2: 4500, 4: 9000, 6: 13000, 8: 17000, 10: 21000 },
  // Drone fees — flat per-project rates (auto + two optional add-ons).
  drone: {
    captureFee: 5000,
    interiorDroneShotsFee: 5000,
    amenityDroneShotsFee: 5000,
  },
  // Video Animation — flat per-duration prices for AI Motion + Cinematic.
  // motionPackage applies when BOTH products are selected simultaneously.
  videoAnimation: {
    aiMotion: { 30: 4500, 60: 8000, 90: 8000 },
    cinematic: { 30: 8000, 60: 10000, 90: 12000 },
    motionPackage: { enabled: true, pct: 10 },
  },
  // Standalone Video — size-first fixed prices used when a video is sold on
  // its own (no Vexos package). Hero prices are per format; add-on prices are
  // per unit; socialCutdownsIncluded is the free cut-down count before the
  // per-clip editing rate applies. Mirrors the STANDALONE_VIDEO singleton.
  standaloneVideo: {
    heroPrices: { hero30: 12500, hero60: 18500, hero90: 22500, master120: 28500 },
    addOnPrices: { unitInterior: 3500, penthouse: 5000, socialCutdownPerClip: 500 },
    socialCutdownsIncluded: 0,
  },
  // Floor Plans — flat per-plan rates + independent volume tiers per type.
  // price3D is the AI Hybrid base rate; traditional3DUpcharge is added per
  // plan when the client chooses the full no-AI 3D pipeline workflow.
  floorPlans: {
    price2D: 500,
    price3D: 750,
    traditional3DUpcharge: 500,
    tier1MinQty: 6,
    tier1Pct: 5,
    tier2MinQty: 11,
    tier2Pct: 10,
  },
  // Bundle incentive system — discount on the Studio Visualization subtotal
  // only. Opt-in: the quote qualifies by module count, the rep applies it
  // (inputs.bundleApplied). See ./bundleOptIn.
  bundle: {
    enabled: true,
    dualPct: 5,
    triplePct: 10,
    fullStackPct: 15,
  },
  // ─── SFH (Single-Family Home) — fixed pricing, no tiers ─────────────
  // SFH engagements use a flat platform offer regardless of unit count.
  // Both values are admin-editable. See `getSFHPricing()` for the
  // resolver used by the calculator's SFH mode.
  sfh: {
    setupFee: 10000,
    monthlySubscription: 1500,
  },
  // ─── Master Plan — flat setup fee; monthly follows the community tiers ─
  masterPlan: {
    setupFee: 30000,
  },
  // ─── Additional Revision Rounds ─────────────────────────────────────
  // The standard visualization package includes `includedRounds` revision
  // rounds. Each additional round costs `additionalPct`% of the NET
  // visualization scope (post-bundle). e.g. 10% of a $30,000 viz scope =
  // $3,000 per additional round.
  revisions: {
    includedRounds: 2,
    additionalPct: 10,
  },
  // ─── On-Site Travel ─────────────────────────────────────────────────
  // Flat per-day fee to send our team out to the client's project. Applied
  // mostly on VEXOS engagements. Rep selects up to `maxDays` days.
  travel: {
    perDayRate: 2500,
    maxDays: 10,
  },
  // ─── Real-Time Configurator (VR tour add-on) ────────────────────────
  // Activation fee once per VR section. Standard categories price each
  // option in order (1st, 2nd, each further); the 1st rides on the
  // activation. Furniture keeps the legacy all-options ladder. See
  // ./vrConfigurator.
  vrConfigurator: {
    activationFee: 1000,
    option1: 0, option2: 700, option3Plus: 500,
    furniture1: 1800, furniture2: 1500, furniture3: 1200, furniture4Plus: 1000,
  },
  // ─── Discount Guardrails (Section 8C) ───────────────────────────────
  // Internal, advisory. Per manually-overridable line (keyed by the same
  // ids as PRICE_OVERRIDE_LINES): the discount off the rate-card price a rep
  // may give freely (`firstStepPct`) and the most it should ever go
  // (`floorPct`). The additional proposal discount has its own pair. List
  // prices are NOT stored here — list is whatever the rate card computes.
  // Placeholder figures; Bernard sets the real ones in the Pricing Admin.
  discountGuardrails: {
    lines: Object.fromEntries(PRICE_OVERRIDE_LINES.map((l) => [
      l.id, { firstStepPct: GUARDRAIL_DEFAULT_FIRST_STEP_PCT, floorPct: GUARDRAIL_DEFAULT_FLOOR_PCT },
    ])),
    additionalDiscountFirstStepPct: 5,
    additionalDiscountFloorPct: 10,
    belowFloorCommissionRule:
      "Below the floor, commission is paid on the sold price minus the shortfall below the floor, and the deal needs Bernard's approval.",
    approvalRequiredBetweenStepAndFloor: true,
  },
  // ─── Complimentary Add-Ons (Section 8D) ─────────────────────────────
  // What a rep may give at no charge, capped at `capPctOfContract`% of the
  // quote's one-time contract value. `value` is per unit and seeded from the
  // rate-card defaults above so "Value $X" matches what the line would cost:
  //   social cutdown    = standaloneVideo.addOnPrices.socialCutdownPerClip
  //   animated keyframe = exterior.animatedKeyframePerKf
  //   render enhancement= COLLATERAL_DEFAULTS.renderEnhancementPer
  //   2D floor plan     = floorPlans.price2D
  //   extra keyframe    = exterior.additionalKeyframePerKf
  //   day-to-night      = exterior.dayToNightPerKf (one keyframe)
  freeAddOns: {
    capPctOfContract: 5,
    items: [
      { id: "socialCutdowns",    label: "Social cutdowns",      value: 500 },
      { id: "animatedKeyframes", label: "Animated keyframes",   value: 500 },
      { id: "renderEnhancement", label: "Render enhancement",   value: 599 },
      { id: "floorPlans2D",      label: "2D floor plans",       value: 500 },
      { id: "extraKeyframe",     label: "Extra keyframe",       value: 1000 },
      { id: "dayToNight",        label: "Day-to-night upgrade", value: 1000 },
    ],
  },
};

/**
 * Merge a saved `exterior` block over the defaults, carrying a pre-per-keyframe
 * day-to-night rate forward.
 *
 * A config saved before the switch only has the flat `dayToNight` project
 * rate. Taking `dayToNightPerKf` straight from ADMIN_DEFAULTS there would
 * silently discard a customised flat rate (a $6,000 override would start
 * quoting at the $1,000 default), so derive the per-keyframe rate from the
 * flat one instead — it only defers to the default when nothing was saved.
 */
function migrateExteriorConfig(saved) {
  const ext = { ...ADMIN_DEFAULTS.exterior, ...(saved || {}) };
  const savedPerKf = Number((saved || {}).dayToNightPerKf);
  if (!(Number.isFinite(savedPerKf) && savedPerKf > 0)) {
    const legacyFlat = Number((saved || {}).dayToNight);
    if (Number.isFinite(legacyFlat) && legacyFlat > 0) {
      ext.dayToNightPerKf = Math.round(legacyFlat / BASE_ORBIT_KEYFRAMES);
    }
  }
  // Configs saved while every keyframe cost the same carry no extra rate. Half
  // the included rate is the rule Bernard set ($1,000 → $500), so derive it
  // rather than dropping a customised rate onto the $500 default.
  const savedExtra = Number((saved || {}).dayToNightExtraPerKf);
  if (!(Number.isFinite(savedExtra) && savedExtra >= 0)) {
    ext.dayToNightExtraPerKf = Math.round(ext.dayToNightPerKf / 2);
  }
  return ext;
}

// Share of the standalone visualization rate that a VEXOS-included rendering
// bills at. Used ONLY to derive the vexos* buckets for configs saved before
// those buckets existed — once a bucket has been saved from the Pricing Admin
// the admin's own numbers stand and this is never applied again.
export const VEXOS_RENDERING_PCT_OF_STANDALONE = 50;

// The price of one rendering inside a VEXOS engagement when Section 3B has
// never been saved (2026-11-02, replacing the 50%-of-standalone derivation).
// A SAVED Section 3B still wins — on prod that means re-entering $1,200 in
// Pricing Admin 3B if it was ever saved at the half rates.
export const VEXOS_INCLUDED_RENDER_RATE = 1200;

/**
 * Merge a saved VEXOS-included rendering bucket over the defaults.
 *
 * A config saved before this bucket existed carries only the standalone
 * table, so pulling defaults straight from ADMIN_DEFAULTS would price VEXOS
 * renderings at half of the CODE default rather than half of the rate the
 * admin actually has saved — a saved $2,400 standalone render would start
 * quoting VEXOS at $900 instead of $1,200. Derive from the saved standalone
 * table instead, carrying its tier thresholds so both cards break at the
 * same volumes.
 */
function migrateVexosRenderingConfig(savedVexos, savedStandalone, standaloneDefaults) {
  const standalone = { ...standaloneDefaults, ...(savedStandalone || {}) };
  // Flat VEXOS rate on every tier; the standalone table only lends its tier
  // breaks so both cards still break at the same volumes.
  const derived = {
    tier1MaxQty: standalone.tier1MaxQty,
    tier1Rate: VEXOS_INCLUDED_RENDER_RATE,
    tier2MaxQty: standalone.tier2MaxQty,
    tier2Rate: VEXOS_INCLUDED_RENDER_RATE,
    tier3Rate: VEXOS_INCLUDED_RENDER_RATE,
  };
  const saved = savedVexos || {};
  const hasSavedRates = ["tier1Rate", "tier2Rate", "tier3Rate"].some((k) => {
    const n = Number(saved[k]);
    return Number.isFinite(n) && n > 0;
  });
  // Nothing saved → the derived half-rate card. Something saved → the saved
  // values win, with `derived` filling any key a partial save left out.
  return hasSavedRates ? { ...derived, ...saved } : derived;
}

// Guardrails: every override line keeps a row (a line added after the config
// was saved picks up the placeholder band), saved rows win field by field.
function mergeDiscountGuardrails(saved) {
  const d = ADMIN_DEFAULTS.discountGuardrails;
  const sv = saved && typeof saved === "object" ? saved : {};
  const savedLines = sv.lines && typeof sv.lines === "object" ? sv.lines : {};
  const lines = {};
  Object.keys(d.lines).forEach((id) => {
    lines[id] = { ...d.lines[id], ...(savedLines[id] || {}) };
  });
  return {
    ...d,
    ...sv,
    lines,
    approvalRequiredBetweenStepAndFloor: sv.approvalRequiredBetweenStepAndFloor === undefined
      ? d.approvalRequiredBetweenStepAndFloor
      : sv.approvalRequiredBetweenStepAndFloor !== false,
  };
}

// Complimentary add-ons: a saved list replaces the seed list wholesale — an
// admin who deleted an item must not see it come back. Only a config saved
// before this section existed gets the seed.
function mergeFreeAddOns(saved) {
  const d = ADMIN_DEFAULTS.freeAddOns;
  const sv = saved && typeof saved === "object" ? saved : {};
  return {
    capPctOfContract: sv.capPctOfContract ?? d.capPctOfContract,
    items: Array.isArray(sv.items)
      ? sv.items.map((a) => ({ ...a }))
      : d.items.map((a) => ({ ...a })),
  };
}

// Deep-merge a (possibly partial) parsed config object with ADMIN_DEFAULTS
// so newly added fields are never undefined and stale partial saves still
// hydrate cleanly. Exported so the shared/remote fetch path can reuse it.
export function mergeAdminStateWithDefaults(parsed) {
  if (!parsed || typeof parsed !== "object") {
    return JSON.parse(JSON.stringify(ADMIN_DEFAULTS));
  }
  const monthly = parsed.monthly || {};
  return {
    setup: { ...ADMIN_DEFAULTS.setup, ...(parsed.setup || {}) },
    monthly: {
      tier1MaxUnits: monthly.tier1MaxUnits ?? ADMIN_DEFAULTS.monthly.tier1MaxUnits,
      tier2MaxUnits: monthly.tier2MaxUnits ?? ADMIN_DEFAULTS.monthly.tier2MaxUnits,
      rates: {
        tier1: { ...ADMIN_DEFAULTS.monthly.rates.tier1, ...((monthly.rates || {}).tier1 || {}) },
        tier2: { ...ADMIN_DEFAULTS.monthly.rates.tier2, ...((monthly.rates || {}).tier2 || {}) },
        tier3: { ...ADMIN_DEFAULTS.monthly.rates.tier3, ...((monthly.rates || {}).tier3 || {}) },
      },
    },
    exterior: migrateExteriorConfig(parsed.exterior),
    conceptualHybrid: { ...ADMIN_DEFAULTS.conceptualHybrid, ...(parsed.conceptualHybrid || {}) },
    interiorRendering: { ...ADMIN_DEFAULTS.interiorRendering, ...(parsed.interiorRendering || {}) },
    interiorVR: { ...ADMIN_DEFAULTS.interiorVR, ...(parsed.interiorVR || {}) },
    exteriorVR: { ...ADMIN_DEFAULTS.exteriorVR, ...(parsed.exteriorVR || {}) },
    amenityRendering: { ...ADMIN_DEFAULTS.amenityRendering, ...(parsed.amenityRendering || {}) },
    vexosInteriorRendering: migrateVexosRenderingConfig(
      parsed.vexosInteriorRendering, parsed.interiorRendering, ADMIN_DEFAULTS.interiorRendering),
    vexosAmenityRendering: migrateVexosRenderingConfig(
      parsed.vexosAmenityRendering, parsed.amenityRendering, ADMIN_DEFAULTS.amenityRendering),
    amenityVRPackages: { ...ADMIN_DEFAULTS.amenityVRPackages, ...(parsed.amenityVRPackages || {}) },
    drone: { ...ADMIN_DEFAULTS.drone, ...(parsed.drone || {}) },
    videoAnimation: {
      aiMotion: { ...ADMIN_DEFAULTS.videoAnimation.aiMotion, ...((parsed.videoAnimation || {}).aiMotion || {}) },
      cinematic: { ...ADMIN_DEFAULTS.videoAnimation.cinematic, ...((parsed.videoAnimation || {}).cinematic || {}) },
      motionPackage: { ...ADMIN_DEFAULTS.videoAnimation.motionPackage, ...((parsed.videoAnimation || {}).motionPackage || {}) },
    },
    standaloneVideo: {
      heroPrices: { ...ADMIN_DEFAULTS.standaloneVideo.heroPrices, ...((parsed.standaloneVideo || {}).heroPrices || {}) },
      addOnPrices: { ...ADMIN_DEFAULTS.standaloneVideo.addOnPrices, ...((parsed.standaloneVideo || {}).addOnPrices || {}) },
      socialCutdownsIncluded: (parsed.standaloneVideo || {}).socialCutdownsIncluded ?? ADMIN_DEFAULTS.standaloneVideo.socialCutdownsIncluded,
    },
    floorPlans: { ...ADMIN_DEFAULTS.floorPlans, ...(parsed.floorPlans || {}) },
    bundle: { ...ADMIN_DEFAULTS.bundle, ...(parsed.bundle || {}) },
    sfh: { ...ADMIN_DEFAULTS.sfh, ...(parsed.sfh || {}) },
    masterPlan: { ...ADMIN_DEFAULTS.masterPlan, ...(parsed.masterPlan || {}) },
    revisions: { ...ADMIN_DEFAULTS.revisions, ...(parsed.revisions || {}) },
    travel: { ...ADMIN_DEFAULTS.travel, ...(parsed.travel || {}) },
    vrConfigurator: { ...ADMIN_DEFAULTS.vrConfigurator, ...(parsed.vrConfigurator || {}) },
    discountGuardrails: mergeDiscountGuardrails(parsed.discountGuardrails),
    freeAddOns: mergeFreeAddOns(parsed.freeAddOns),
    // Marketing & Collateral overrides (Tab 2) — flow through untouched.
    ...(parsed.marketing ? { marketing: parsed.marketing } : {}),
    ...(parsed.collateral ? { collateral: parsed.collateral } : {}),
  };
}

// Synchronous bootstrap — reads localStorage if present (legacy / pre-DB
// migration cache) and returns defaults otherwise. The remote shared config
// is loaded asynchronously by the calculator on mount via
// `fetchSharedAdminState()` which then replaces this initial state.
export function loadAdminState() {
  try {
    const raw = localStorage.getItem(ADMIN_STORAGE_KEY);
    if (!raw) return JSON.parse(JSON.stringify(ADMIN_DEFAULTS));
    return mergeAdminStateWithDefaults(JSON.parse(raw));
  } catch {
    return JSON.parse(JSON.stringify(ADMIN_DEFAULTS));
  }
}

// Legacy localStorage saver — retained ONLY as the migration source. The
// active save path is `saveSharedAdminState()` which writes to MongoDB.
// Calling this no longer persists to localStorage — it just applies the
// state in-memory so the calculator re-renders.
export function saveAdminState(state) {
  applyAdminToConfigs(state);
}

export function resetAdminState() {
  localStorage.removeItem(ADMIN_STORAGE_KEY);
  applyAdminToConfigs(ADMIN_DEFAULTS);
}

// ─── localStorage migration helpers ─────────────────────────────────────
// `peekLegacyAdminState()` returns the raw legacy localStorage payload (or
// `null` if none) so the calculator can decide whether to one-time
// migrate it to the shared backend doc. `clearLegacyAdminState()` wipes
// the local cache once migration succeeds or when the user is non-admin.
export function peekLegacyAdminState() {
  try {
    const raw = localStorage.getItem(ADMIN_STORAGE_KEY);
    return raw ? mergeAdminStateWithDefaults(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function clearLegacyAdminState() {
  try { localStorage.removeItem(ADMIN_STORAGE_KEY); } catch {}
}

export function loadAdminPin() {
  return localStorage.getItem(ADMIN_PIN_KEY) || DEFAULT_PIN;
}

export function saveAdminPin(pin) {
  localStorage.setItem(ADMIN_PIN_KEY, String(pin));
}

/* ════════════════════════════════════════════════════════════════════════
 *  SHARED PRICING CONFIG — MongoDB-backed (multi-user sync)
 *
 *  The single source of truth lives at `GET/PUT /api/vexos-pricing-config`.
 *  Every authenticated user can READ; only admin/COO/Owner roles can WRITE.
 *  These helpers wrap the API calls + apply the resulting state to the
 *  in-memory config singletons used by the calculator.
 * ═══════════════════════════════════════════════════════════════════════ */

// Roles permitted to mutate shared pricing — mirrors backend _ADMIN_ROLES
// in `routers/vexos_pricing_config.py`. Reads are open to any authed user.
const _VEXOS_PRICING_ADMIN_ROLES = new Set([
  "COO", "Admin", "Owner",
  "admin", "coo", "owner",
  "ceo", "founder", "super_admin",
]);

export function isVexosPricingAdmin(user) {
  if (!user) return false;
  return _VEXOS_PRICING_ADMIN_ROLES.has(user.role);
}

// Fetch the shared config from backend. Returns `{ state, meta }` where:
//   state = ADMIN_DEFAULTS deep-merged with whatever the DB has (or pure
//           defaults if no shared config exists yet)
//   meta  = { exists, updatedAt, updatedBy, updatedByName }
export async function fetchSharedAdminState() {
  try {
    const res = await apiClient.get("/vexos-pricing-config");
    const data = res?.data || {};
    const state = data.exists && data.config
      ? mergeAdminStateWithDefaults(data.config)
      : JSON.parse(JSON.stringify(ADMIN_DEFAULTS));
    return {
      state,
      meta: {
        exists: !!data.exists,
        updatedAt: data.updated_at || null,
        updatedBy: data.updated_by || null,
        updatedByName: data.updated_by_name || null,
      },
    };
  } catch (e) {
    // Offline / 401 / 500 — fall back to defaults so calculator still works.
    return {
      state: JSON.parse(JSON.stringify(ADMIN_DEFAULTS)),
      meta: { exists: false, updatedAt: null, updatedBy: null, updatedByName: null, error: e?.message || "fetch failed" },
    };
  }
}

// Save the shared config (admin only). Returns the updated meta block on
// success, throws on failure so callers can surface the error.
export async function saveSharedAdminState(state) {
  const res = await apiClient.put("/vexos-pricing-config", { config: state });
  const data = res?.data || {};
  applyAdminToConfigs(state);
  return {
    exists: !!data.exists,
    updatedAt: data.updated_at || null,
    updatedBy: data.updated_by || null,
    updatedByName: data.updated_by_name || null,
  };
}

// Active team-member count — used in the Pricing Admin Save chip.
export async function fetchPricingAudienceCount() {
  try {
    const res = await apiClient.get("/vexos-pricing-config/audience");
    return Number(res?.data?.count) || 0;
  } catch {
    return 0;
  }
}

// ─── Volume-tier lookup ──────────────────────────────────────────────────
// volumeTiers shape: [{ maxQty: 10, ratePerUnit: 150 }, { maxQty: 25, ratePerUnit: 130 }, { maxQty: null, ratePerUnit: 110 }]
export function lookupVolumeRate(qty, volumeTiers) {
  const n = Math.max(0, Number(qty) || 0);
  if (n === 0 || !Array.isArray(volumeTiers) || volumeTiers.length === 0) return 0;
  const tier = volumeTiers.find(t => t.maxQty === null || t.maxQty === undefined || n <= t.maxQty);
  return tier ? Number(tier.ratePerUnit) || 0 : 0;
}

// ─── Rendering rate-card selection ───────────────────────────────────────
// Renderings sell off TWO rate cards: the standalone visualization card
// (À La Carte quotes) and the VEXOS-included card, which runs at half those
// rates by default. Every other line item has a single card.
//
// `vexosIncluded` is true whenever the renders ride inside a VEXOS engagement
// — the VEXOS tab with the master toggle ON — and false for a standalone
// visualization quote. Falls back to the standalone card when the VEXOS card
// has been wiped to zeros, so a blank bucket can never quote renders at $0.
export function renderingTiers(renderingConfig, vexosIncluded) {
  const cfg = renderingConfig || {};
  if (!vexosIncluded) return cfg.volumeTiers;
  const vexosTiers = cfg.vexosVolumeTiers;
  const usable = Array.isArray(vexosTiers)
    && vexosTiers.some((t) => (Number(t?.ratePerUnit) || 0) > 0);
  return usable ? vexosTiers : cfg.volumeTiers;
}

export function activeVolumeTierIndex(qty, volumeTiers) {
  const n = Math.max(0, Number(qty) || 0);
  if (n === 0 || !Array.isArray(volumeTiers)) return -1;
  return volumeTiers.findIndex(t => t.maxQty === null || t.maxQty === undefined || n <= t.maxQty);
}

// ─── Apply admin state to exported configs (mutates in place) ────────────
// This is called on module load (using cached localStorage state) and again
// every time the admin saves. Calculator listens on a version counter so its
// useMemo recomputes after a save.
/**
 * Day-to-night per-keyframe rate out of a saved admin config.
 *
 * Configs saved before day-to-night went per-keyframe only carry the flat
 * project rate (`dayToNight`, historically $4,000 for a 4-keyframe base
 * orbit). Deriving flat ÷ BASE_ORBIT_KEYFRAMES keeps those configs pricing a
 * plain base-orbit quote at exactly the number they were saved with, while
 * any extra keyframes and suborbits now scale on top of it.
 */
export function resolveDayToNightPerKf(exterior) {
  const ext = exterior || {};
  const perKf = Number(ext.dayToNightPerKf);
  if (Number.isFinite(perKf) && perKf > 0) return perKf;
  const flat = Number(ext.dayToNight);
  if (Number.isFinite(flat) && flat > 0) return Math.round(flat / BASE_ORBIT_KEYFRAMES);
  return 0;
}

/** Half the included rate when a config predates the two-tier split. */
export function resolveDayToNightExtraPerKf(exterior) {
  const ext = exterior || {};
  const extra = Number(ext.dayToNightExtraPerKf);
  if (Number.isFinite(extra) && extra >= 0) return extra;
  return Math.round(resolveDayToNightPerKf(ext) / 2);
}

/**
 * THE one day-to-night costing seam. Everything that shows a day-to-night
 * figure — the calculator, the proposal, the PDF — must come through here, or
 * two surfaces will quote the same orbit differently.
 *
 * `subOrbits` is the list of per-suborbit EXTRA keyframe counts the builder
 * keeps (its length is the suborbit count), which is why the suborbit's own
 * included keyframes are added from SUBORBIT_INCLUDED_KEYFRAMES rather than
 * read off the entry.
 *
 * Returns the total plus the breakdown, because "why is this $7,500?" is a
 * question a rep gets asked on the call.
 */
export function dayToNightBreakdown({
  baseExtraKf = 0,
  subOrbits = [],
  includedPerKf = 0,
  extraPerKf = 0,
} = {}) {
  const orbits = Array.isArray(subOrbits) ? subOrbits : [];
  const baseExtra = Math.max(0, Number(baseExtraKf) || 0);
  const subExtra = orbits.reduce((sum, n) => sum + Math.max(0, Number(n) || 0), 0);

  const includedKf = BASE_ORBIT_KEYFRAMES + orbits.length * SUBORBIT_INCLUDED_KEYFRAMES;
  const extraKf = baseExtra + subExtra;

  const includedCost = includedKf * includedPerKf;
  const extraCost = extraKf * extraPerKf;

  return {
    total: includedCost + extraCost,
    includedKf,
    extraKf,
    totalKf: includedKf + extraKf,
    includedCost,
    extraCost,
    // Per-orbit figures, so the UI can say "$4,000 base orbit · $2,000 suborbit".
    baseOrbitIncludedCost: BASE_ORBIT_KEYFRAMES * includedPerKf,
    subOrbitIncludedCost: SUBORBIT_INCLUDED_KEYFRAMES * includedPerKf,
    subOrbitCount: orbits.length,
  };
}

function buildVolumeTiers(section) {
  return [
    { maxQty: Number(section.tier1MaxQty) || 0, ratePerUnit: Number(section.tier1Rate) || 0 },
    { maxQty: Number(section.tier2MaxQty) || 0, ratePerUnit: Number(section.tier2Rate) || 0 },
    { maxQty: null, ratePerUnit: Number(section.tier3Rate) || 0 },
  ];
}

export function applyAdminToConfigs(state) {
  // Setup fee tiers (shared across Condo + Community).
  const setupT = [
    { minUnits: 0,                                         maxUnits: state.setup.tier1MaxUnits,            fee: state.setup.tier1Fee, label: `Under ${state.setup.tier1MaxUnits + 1}` },
    { minUnits: state.setup.tier1MaxUnits + 1,             maxUnits: state.setup.tier2MaxUnits,            fee: state.setup.tier2Fee, label: `${state.setup.tier1MaxUnits + 1}–${state.setup.tier2MaxUnits}` },
    { minUnits: state.setup.tier2MaxUnits + 1,             maxUnits: null,                                  fee: state.setup.tier3Fee, label: `${state.setup.tier2MaxUnits + 1}+` },
  ];
  SETUP_FEE_TIERS.splice(0, SETUP_FEE_TIERS.length, ...setupT);
  COMMUNITY_SETUP_FEE_TIERS.splice(0, COMMUNITY_SETUP_FEE_TIERS.length, ...setupT.map(t => ({
    minLots: t.minUnits, maxLots: t.maxUnits, fee: t.fee, label: t.label,
  })));

  // Monthly subscription — 3×3 matrix (tier × term). Store the matrix on
  // the exported MONTHLY_SUBSCRIPTION_TIERS array as `ratesByTerm` per tier.
  const monthlyT = [
    { minUnits: 0,                                  maxUnits: state.monthly.tier1MaxUnits,           ratesByTerm: { ...state.monthly.rates.tier1 }, label: `Under ${state.monthly.tier1MaxUnits + 1}` },
    { minUnits: state.monthly.tier1MaxUnits + 1,    maxUnits: state.monthly.tier2MaxUnits,           ratesByTerm: { ...state.monthly.rates.tier2 }, label: `${state.monthly.tier1MaxUnits + 1}–${state.monthly.tier2MaxUnits}` },
    { minUnits: state.monthly.tier2MaxUnits + 1,    maxUnits: null,                                   ratesByTerm: { ...state.monthly.rates.tier3 }, label: `${state.monthly.tier2MaxUnits + 1}+` },
  ];
  MONTHLY_SUBSCRIPTION_TIERS.splice(0, MONTHLY_SUBSCRIPTION_TIERS.length, ...monthlyT);
  COMMUNITY_MONTHLY_SUBSCRIPTION_TIERS.splice(0, COMMUNITY_MONTHLY_SUBSCRIPTION_TIERS.length, ...monthlyT.map(t => ({
    minLots: t.minUnits, maxLots: t.maxUnits, ratesByTerm: t.ratesByTerm, label: t.label,
  })));

  // Exterior add-ons (Condo + Community get the same values).
  EXTERIOR_ADDONS.dayToNight.pricePerKeyframe          = resolveDayToNightPerKf(state.exterior);
  EXTERIOR_ADDONS.dayToNight.pricePerExtraKeyframe     = resolveDayToNightExtraPerKf(state.exterior);
  EXTERIOR_ADDONS.additionalKeyframes.pricePerKeyframe = Number(state.exterior.additionalKeyframePerKf) || 0;
  EXTERIOR_ADDONS.animatedKeyframes.pricePerKeyframe   = Number(state.exterior.animatedKeyframePerKf) || 0;
  EXTERIOR_ADDONS.masterOrbit.pricePerOrbit            = Number(state.exterior.masterOrbitPerOrbit) || 0;
  EXTERIOR_ADDONS.vexosCoverLandingPage.price          = Number(state.exterior.vexosCoverLandingPage) || 0;
  COMMUNITY_EXTERIOR_ADDONS.dayToNight.pricePerKeyframe         = EXTERIOR_ADDONS.dayToNight.pricePerKeyframe;
  COMMUNITY_EXTERIOR_ADDONS.dayToNight.pricePerExtraKeyframe    = EXTERIOR_ADDONS.dayToNight.pricePerExtraKeyframe;
  COMMUNITY_EXTERIOR_ADDONS.additionalKeyframes.pricePerKeyframe = EXTERIOR_ADDONS.additionalKeyframes.pricePerKeyframe;
  COMMUNITY_EXTERIOR_ADDONS.animatedKeyframes.pricePerKeyframe   = EXTERIOR_ADDONS.animatedKeyframes.pricePerKeyframe;
  COMMUNITY_EXTERIOR_ADDONS.masterOrbit.pricePerOrbit            = EXTERIOR_ADDONS.masterOrbit.pricePerOrbit;
  if (COMMUNITY_EXTERIOR_ADDONS.vexosCoverLandingPage) {
    COMMUNITY_EXTERIOR_ADDONS.vexosCoverLandingPage.price = EXTERIOR_ADDONS.vexosCoverLandingPage.price;
  }

  // Conceptual Rendering Hybrid Workflow — flat per-image rate.
  CONCEPTUAL_MODELING.pricePerRender =
    Number((state.conceptualHybrid || ADMIN_DEFAULTS.conceptualHybrid).pricePerRender) || 0;

  // Volume tiers for renderings + interior VR (both Condo and Community).
  INTERIOR_RENDERING.volumeTiers   = buildVolumeTiers(state.interiorRendering);
  INTERIOR_VR.volumeTiers          = buildVolumeTiers(state.interiorVR);
  // Exterior VR — single shared rate table for Condo / SFH / Community.
  EXTERIOR_VR.volumeTiers          = buildVolumeTiers(state.exteriorVR || ADMIN_DEFAULTS.exteriorVR);
  AMENITY_RENDERING.volumeTiers    = buildVolumeTiers(state.amenityRendering);
  HOME_MODEL_RENDERING.volumeTiers       = INTERIOR_RENDERING.volumeTiers;
  HOME_MODEL_VR.volumeTiers              = INTERIOR_VR.volumeTiers;
  COMMUNITY_AMENITY_RENDERING.volumeTiers = AMENITY_RENDERING.volumeTiers;

  // Second rendering rate card — the one VEXOS-included renders bill off.
  // Lives alongside `volumeTiers` on the same config objects; `renderingTiers()`
  // picks between the two per quote context. Renderings are the only line
  // items with two cards; VR, floor plans and video have one each.
  INTERIOR_RENDERING.vexosVolumeTiers = buildVolumeTiers(
    state.vexosInteriorRendering || ADMIN_DEFAULTS.vexosInteriorRendering);
  AMENITY_RENDERING.vexosVolumeTiers = buildVolumeTiers(
    state.vexosAmenityRendering || ADMIN_DEFAULTS.vexosAmenityRendering);
  HOME_MODEL_RENDERING.vexosVolumeTiers        = INTERIOR_RENDERING.vexosVolumeTiers;
  COMMUNITY_AMENITY_RENDERING.vexosVolumeTiers = AMENITY_RENDERING.vexosVolumeTiers;

  // Amenity VR — flat package prices per row selection (4 / 6 / 8 / 10 pts).
  AMENITY_VR.packages = { ...state.amenityVRPackages };
  COMMUNITY_AMENITY_VR.packages = { ...state.amenityVRPackages };

  // Drone fees — three flat per-project rates.
  const drone = state.drone || ADMIN_DEFAULTS.drone;
  DRONE.captureFee             = Number(drone.captureFee)             || 0;
  DRONE.interiorDroneShotsFee  = Number(drone.interiorDroneShotsFee)  || 0;
  DRONE.amenityDroneShotsFee   = Number(drone.amenityDroneShotsFee)   || 0;

  // Video Animation — flat per-duration rates.
  VIDEO_ANIMATION.aiMotion.durations = { ...((state.videoAnimation || {}).aiMotion || ADMIN_DEFAULTS.videoAnimation.aiMotion) };
  VIDEO_ANIMATION.cinematic.durations = { ...((state.videoAnimation || {}).cinematic || ADMIN_DEFAULTS.videoAnimation.cinematic) };
  // Motion Package (bonus discount when BOTH video products are selected).
  const mp = (state.videoAnimation || {}).motionPackage || ADMIN_DEFAULTS.videoAnimation.motionPackage;
  VIDEO_ANIMATION.motionPackage.enabled = mp.enabled !== false;
  VIDEO_ANIMATION.motionPackage.pct = Number(mp.pct) || 0;

  // Standalone Video — size-first hero + add-on prices. Metadata (tiers,
  // add-on labels, lineTypes) is static; only the priced fields + the
  // included-cut-down count are admin-editable and reassigned here.
  const sv = state.standaloneVideo || ADMIN_DEFAULTS.standaloneVideo;
  STANDALONE_VIDEO.heroPrices = { ...ADMIN_DEFAULTS.standaloneVideo.heroPrices, ...(sv.heroPrices || {}) };
  STANDALONE_VIDEO.addOnPrices = { ...ADMIN_DEFAULTS.standaloneVideo.addOnPrices, ...(sv.addOnPrices || {}) };
  STANDALONE_VIDEO.socialCutdownsIncluded = Number(sv.socialCutdownsIncluded) || 0;

  // Floor Plans — flat per-plan rates + per-type volume tiers.
  FLOOR_PLANS.price2D = Number((state.floorPlans || {}).price2D) || 0;
  FLOOR_PLANS.price3D = Number((state.floorPlans || {}).price3D) || 0;
  FLOOR_PLANS.traditional3DUpcharge = Number((state.floorPlans || {}).traditional3DUpcharge) || 0;
  FLOOR_PLANS.volumeTiers = {
    tier1MinQty: Number((state.floorPlans || {}).tier1MinQty) || 0,
    tier1Pct: Number((state.floorPlans || {}).tier1Pct) || 0,
    tier2MinQty: Number((state.floorPlans || {}).tier2MinQty) || 0,
    tier2Pct: Number((state.floorPlans || {}).tier2Pct) || 0,
  };

  // Bundle config — exposed for the calc memo.
  BUNDLE_CONFIG.enabled      = state.bundle.enabled;
  BUNDLE_CONFIG.dualPct      = Number(state.bundle.dualPct) || 0;
  BUNDLE_CONFIG.triplePct    = Number(state.bundle.triplePct) || 0;
  BUNDLE_CONFIG.fullStackPct = Number(state.bundle.fullStackPct) || 0;

  // SFH (Single-Family Home) — fixed pricing singleton. Falls back to the
  // canonical defaults whenever the admin block is missing/blank so legacy
  // saved configs (pre-SFH feature) hydrate cleanly.
  const sfh = state.sfh || ADMIN_DEFAULTS.sfh;
  SFH_CONFIG.setupFee = Number(sfh.setupFee) || SFH_DEFAULTS.setupFee;
  SFH_CONFIG.monthlySubscription = Number(sfh.monthlySubscription) || SFH_DEFAULTS.monthlySubscription;

  const masterPlan = state.masterPlan || ADMIN_DEFAULTS.masterPlan;
  MASTER_PLAN_CONFIG.setupFee = Number(masterPlan.setupFee) || MASTER_PLAN_DEFAULTS.setupFee;

  // Additional revision rounds — included count + per-round % of net viz.
  const rev = state.revisions || ADMIN_DEFAULTS.revisions;
  REVISIONS_CONFIG.includedRounds = Number(rev.includedRounds) || 0;
  REVISIONS_CONFIG.additionalPct = Number(rev.additionalPct) || 0;

  // On-site travel — flat per-day rate + max selectable days.
  const trv = state.travel || ADMIN_DEFAULTS.travel;
  TRAVEL_CONFIG.perDayRate = Number(trv.perDayRate) || 0;
  TRAVEL_CONFIG.maxDays = Number(trv.maxDays) || 0;

  // Real-Time Configurator — activation fee + per-option ladders.
  const vrc = { ...ADMIN_DEFAULTS.vrConfigurator, ...(state.vrConfigurator || {}) };
  VR_CONFIGURATOR.activationFee = Number(vrc.activationFee) || 0;
  VR_CONFIGURATOR.standardRates = [vrc.option1, vrc.option2, vrc.option3Plus].map((v) => Number(v) || 0);
  VR_CONFIGURATOR.furnitureRates = [vrc.furniture1, vrc.furniture2, vrc.furniture3, vrc.furniture4Plus].map((v) => Number(v) || 0);

  // Discount guardrails + complimentary add-ons — internal, money-neutral.
  const dg = mergeDiscountGuardrails(state.discountGuardrails);
  Object.keys(DISCOUNT_GUARDRAILS).forEach((k) => { delete DISCOUNT_GUARDRAILS[k]; });
  Object.assign(DISCOUNT_GUARDRAILS, dg);
  const fa = mergeFreeAddOns(state.freeAddOns);
  FREE_ADD_ONS.capPctOfContract = Number(fa.capPctOfContract) || 0;
  FREE_ADD_ONS.items = fa.items
    .filter((a) => a && String(a.id || "").trim() && String(a.label || "").trim())
    .map((a) => ({ id: String(a.id).trim(), label: String(a.label).trim(), value: Math.max(0, Math.round(Number(a.value) || 0)) }));
}

// ─── Discount guardrails singleton (Section 8C) ──────────────────────────
// Mutated in place by applyAdminToConfigs(). Read by ./guardrails helpers.
export const DISCOUNT_GUARDRAILS = {};

// ─── Complimentary add-ons singleton (Section 8D) ────────────────────────
export const FREE_ADD_ONS = { capPctOfContract: 5, items: [] };

// ─── Bundle config singleton ─────────────────────────────────────────────
export const BUNDLE_CONFIG = {
  enabled: true,
  dualPct: 5,
  triplePct: 10,
  fullStackPct: 15,
};

// ─── Additional Revisions config singleton ───────────────────────────────
// `includedRounds` ship with the standard package; each round beyond that
// costs `additionalPct`% of the net visualization scope. Mutated in place by
// applyAdminToConfigs() so the calculator's useMemo picks up admin edits.
export const REVISIONS_CONFIG = {
  includedRounds: 2,
  additionalPct: 10,
};

// Cost of `rounds` additional revision rounds against a net viz scope.
export function revisionRoundCost(vizNet) {
  return Math.round((Number(vizNet) || 0) * ((Number(REVISIONS_CONFIG.additionalPct) || 0) / 100));
}

// ─── On-Site Travel config singleton ─────────────────────────────────────
export const TRAVEL_CONFIG = {
  perDayRate: 2500,
  maxDays: 10,
};

// Returns { activeCount, tier, label, pct } based on active module count —
// the tier the quote QUALIFIES for. Whether it is on the quote is the rep's
// call (appliedBundle() in ./bundleOptIn).
// 6-module system (Exterior, Renderings, VR Tours, Amenities, Video, Floor Plans).
// Thresholds: 2 → Dual, 3 → Triple, 5+ → Full Stack.
export function evaluateBundle(activeCount) {
  if (!BUNDLE_CONFIG.enabled) return { activeCount, tier: 0, label: null, pct: 0 };
  if (activeCount >= 5) return { activeCount, tier: 3, label: "Full Stack Bundle",      pct: BUNDLE_CONFIG.fullStackPct };
  if (activeCount === 4) return { activeCount, tier: 2, label: "Triple Experience Bundle", pct: BUNDLE_CONFIG.triplePct };
  if (activeCount === 3) return { activeCount, tier: 2, label: "Triple Experience Bundle", pct: BUNDLE_CONFIG.triplePct };
  if (activeCount === 2) return { activeCount, tier: 1, label: "Dual Experience Bundle",   pct: BUNDLE_CONFIG.dualPct };
  return { activeCount, tier: 0, label: null, pct: 0 };
}

// Look up amenity VR package price by point count (0 = $0; 4/6/8/10 = packages).
export function amenityVRPackagePrice(points) {
  const n = Number(points) || 0;
  if (n === 0) return 0;
  return Number((AMENITY_VR.packages || {})[n]) || 0;
}

// Run on import so the calculator sees admin overrides on first render.
applyAdminToConfigs(loadAdminState());

// ───────────────────────────────────────────────────────────────────────
//  MARKETING & COLLATERAL (Tab 2)
//  Carryover from legacy TRP calculator — admin-overridable just like the
//  visualization configs above. Defaults match the spec verbatim.
// ───────────────────────────────────────────────────────────────────────
export const MARKETING_DEFAULTS = {
  // Legacy flat fees — retained for backwards-compatibility with quotes
  // saved before the tiered structure was introduced. They mirror the
  // Tier-1 (Standard) values and are no longer authoritative.
  setupFee: 5500,
  monthlyServiceFee: 3500,
  // ── Section 9A · Campaign Fees, tiered by unit/lot count ─────────────
  // Three tiers — Standard, Volume, Scale. Tier 3 has no upper bound.
  // Thresholds are admin-editable upper inclusive bounds.
  campaignFeeTiers: [
    { id: "tier1", label: "Tier 1 — Standard", upperBound: 49,    setupFee: 5500, monthlyServiceFee: 3500 },
    { id: "tier2", label: "Tier 2 — Volume",   upperBound: 150,   setupFee: 6500, monthlyServiceFee: 4500 },
    { id: "tier3", label: "Tier 3 — Scale",    upperBound: null,  setupFee: 7500, monthlyServiceFee: 5500 },
  ],
  // Sliders
  unitsDefault: 50,
  unitsMin: 4,
  unitsMax: 5000,
  timelineDefaultMonths: 12,
  timelineMinMonths: 1,
  timelineMaxMonths: 60,
  conversionDefaultPct: 1.5,
  conversionMinPct: 1,
  conversionMaxPct: 5,
};

// Resolve which campaign-fee tier applies for a given unit/lot count.
// Returns the tier object (with index attached) — never null. Falls back
// to the last tier (no upper bound) if no tier matches.
export function resolveCampaignFeeTier(units, tiers) {
  const list = Array.isArray(tiers) && tiers.length > 0 ? tiers : MARKETING_DEFAULTS.campaignFeeTiers;
  const n = Number(units) || 0;
  for (let i = 0; i < list.length; i += 1) {
    const t = list[i];
    if (t.upperBound == null || n <= Number(t.upperBound)) {
      return { ...t, index: i, tierNumber: i + 1 };
    }
  }
  const last = list[list.length - 1];
  return { ...last, index: list.length - 1, tierNumber: list.length };
}

// CPL tiers — `upperBound` is the inclusive top of the range. Tier 4 has
// `upperBound: null` meaning "and above". Both `rate` and `upperBound` are
// admin-editable per spec.
export const PRESALE_CPL_TIERS = [
  { id: "tier1", label: "Below $600K",      upperBound: 600_000,    rate: 35  },
  { id: "tier2", label: "$600K – $1.2M",    upperBound: 1_200_000,  rate: 60  },
  { id: "tier3", label: "$1.2M – $2.5M",    upperBound: 2_500_000,  rate: 75  },
  { id: "tier4", label: "$2.5M+",           upperBound: null,       rate: 100 },
];
export const PRELEASE_CPL_TIERS = [
  { id: "tier1", label: "Below $1,500/mo",          upperBound: 1500, rate: 10 },
  { id: "tier2", label: "$1,500 – $2,500/mo",       upperBound: 2500, rate: 15 },
  { id: "tier3", label: "$2,500 – $4,500/mo",       upperBound: 4500, rate: 25 },
  { id: "tier4", label: "$4,500+/mo",               upperBound: null, rate: 40 },
];

export const COLLATERAL_DEFAULTS = {
  landingPage:           3000,
  fullWebsite:           7500,
  brandingPackage:       10000,
  investmentPresentation:4000,
  developmentFlipbook:   7500,
  billboardStatic:       1500,
  billboardAnimated:     3000,
  unitInventorySheet:    750,
  animatedRendersPer:    899,
  renderEnhancementPer:  599,
  // AI Social Media Retainer monthly rates
  retainer6:             5394,
  retainer12:            9708,
  retainer18:            13755,
};

// Pull marketing + collateral overrides from the same admin storage layer
// used by the existing pricing admin (localStorage). Spec section 9 + 10.
function pullMarketingOverrides(s) {
  const m = s?.marketing || {};
  // Deep-merge campaignFeeTiers so admins can override individual tiers
  // without wiping the array.
  const adminTiers = Array.isArray(m.campaignFeeTiers) ? m.campaignFeeTiers : null;
  const mergedTiers = adminTiers
    ? MARKETING_DEFAULTS.campaignFeeTiers.map((def, i) => ({ ...def, ...(adminTiers[i] || {}) }))
    : MARKETING_DEFAULTS.campaignFeeTiers;
  return { ...MARKETING_DEFAULTS, ...m, campaignFeeTiers: mergedTiers };
}
function pullCollateralOverrides(s) {
  const c = s?.collateral || {};
  return { ...COLLATERAL_DEFAULTS, ...c };
}
function pullCPLOverrides(s) {
  const m = s?.marketing || {};
  return {
    preSale:  Array.isArray(m.presaleCpl)  ? m.presaleCpl  : PRESALE_CPL_TIERS,
    preLease: Array.isArray(m.preleaseCpl) ? m.preleaseCpl : PRELEASE_CPL_TIERS,
  };
}

// Public accessors used by the calculator UI + math.
export function getMarketingConfig() {
  return pullMarketingOverrides(loadAdminState());
}
export function getCollateralConfig() {
  return pullCollateralOverrides(loadAdminState());
}
export function getCPLConfig() {
  return pullCPLOverrides(loadAdminState());
}

// Resolve the CPL rate for a given campaignType + tier id. Returns 0 if no
// tier is selected so the calculator gracefully renders $0 until the rep
// picks a tier.
export function resolveCPL(campaignType, tierId) {
  const cfg = getCPLConfig();
  const list = campaignType === "pre-lease" ? cfg.preLease : cfg.preSale;
  const t = list.find((x) => x.id === tierId);
  return Number(t?.rate || 0);
}

